// Copyright (c) 2020-2021, 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { Button, Card, Grid, Tooltip, alpha } from '@mui/material';
import { RouteComponentProps, NavLink } from 'react-router-dom';
import Axios, { CancelTokenSource } from 'axios';
import { observer } from 'mobx-react';
import cx from 'classnames';

import { IMPORT_INFORMATION_TYPES_LABELS, EXPORT_INFORMATION_TYPES_LABELS, REPORT_INFORMATION_TYPES_LABELS, ImportInformationType, ExportInformationType, ReportInformationType } from '@shared/constants/data-management';
import { getQueries, setQueries, deleteQueries, getDefaultError, errObj2Str } from '@shared/utils/common';
import CloseIcon from '@shared/icons/Close';
import EmptyDataManagementIcon from '@shared/icons/EmptyDataManagement';
import DataImport from './components/DataImport';
import Message from '@shared/components/Message';
import { FunctionType } from '@modules/Private/submodules/DataManagement';
import Column, { ColumnProps } from './components/Column';
import InformationType from './components/InformationType';
import Preparing from './components/Preparing';
import container from '@core/di';
import DataManagementStore from '@shared/stores/data-management';
import { downloadFile } from '@shared/utils/file';
import { showNotification, NotificationType } from '@shared/components/Notification';

import Flex from '@shared/components/Flex';
import Select, { SerializerType } from '@shared/components/Select';
import Field from '@shared/components/Form/components/Field';
import { action, makeObservable, observable } from 'mobx';
import { AquaStore, OrdersStore } from '@shared/stores';
import { ListRequestParams } from '@shared/types/services';
import { ORDER_FIELDS, ORDER_MAX_COUNT } from '@shared/constants/order';
import { SortingType } from '@shared/constants/services';
import AuthStore from '@core/stores/auth';
import themev5 from '@core/themev5';
import Sync from '@mui/icons-material/Sync';
import Loading from '@shared/components/Loading';


type Params = {
  functionType?: string;
};

interface ColumnConfig {
  id: string;
  visible: boolean;
  component: JSX.Element;
  heading: ColumnProps['heading'];
  subHeading?: ColumnProps['subHeading'];
  classes?: Partial<ColumnProps['classes']>;
  onClose?: ColumnProps['onClose'];
}

const normalizeInformationType = (types: { [key: number]: string }) => {
  return Object.keys(types).map((type) => {
    return {
      type: Number(type),
      text: types[type]
    };
  });
};

// Array of type ids and type names for the three functions.
const exportInformationTypes = normalizeInformationType(EXPORT_INFORMATION_TYPES_LABELS);
const importInformationTypes = normalizeInformationType(IMPORT_INFORMATION_TYPES_LABELS);
const reportInformationTypes = normalizeInformationType(REPORT_INFORMATION_TYPES_LABELS);

// Prop interface for the main class.
export interface DataManagementChoiceProps extends RouteComponentProps<Params> {
  className?: string;
  rootURL: string;
}

// Class to display the "File" page which consists of three panels
//   Select function (import/export/reports) 
//   Select info type (depending on the type of the first panel)
//   Browse button (depending on the type of the first two panel)
@observer
class DataManagementChoice extends React.Component<DataManagementChoiceProps> {
  @observable orders: {id: number, label: string}[] = [];        // List of order id and name.
  @observable selectedOrderId: number = 0;                       // The currently selected orderId.
  @observable syncing = false;                                   // Flag to indicate if the sync is in progress.

  private dataManagementStore = container.get<DataManagementStore>(DataManagementStore.diToken);
  private ordersStore = container.get<OrdersStore>(OrdersStore.diToken);
  private authStore = container.get<AuthStore>(AuthStore.diToken);
  private aquaStore = container.get<AquaStore>(AquaStore.diToken);
  private cancelTokenSource: CancelTokenSource | undefined;

  constructor(props: DataManagementChoiceProps) {
    super(props);
    this.initialize();
    makeObservable(this);
  }

  // Get the data for the order selection box for "Reports"
  private initialize = async () => {
    try {
      // Setup queries struct for getList
      const queries: ListRequestParams = {
        pagination: {
          page: 0,
          pageSize: ORDER_MAX_COUNT,      
        },
        sorting: {
          field: ORDER_FIELDS.orderName,
          type: SortingType.asc
        },
        flag: 1
      };

      // Load orders from DB.
      await this.ordersStore.getList(queries);
      
      // Get list of orderIds and labels from the order list.
      this.orders = this.ordersStore.list.length == 0 ? [] : this.ordersStore.list.map(item => ({id: item.id, label: item.orderName}));
      if (this.orders.length == 1) {
        this.selectedOrderId = this.orders[0].id;
      }
    } 
    catch (err) {
      showNotification(errObj2Str(err), NotificationType.error);
    }
  }

  // Construct the selection column for "Export", "Import", and "Reports" functions.
  private get functionColumns(): Array<ColumnConfig> {
    const { match: { params: { functionType } } } = this.props;
    const classes = withStyles.getClasses(this.props) as Record<string, any>;

    if (!functionType) {
      return [];
    }

    // Info type (Assets, Platforms, ...) for Import (only).
    const { type } = getQueries();
    const currentInformationType = Number(type);

    console.log("**** functionType:", functionType, ", curType:", type, currentInformationType);

    // Panel for Export
    const exportColumns: Array<ColumnConfig> = [
      {
        id: 'Export',
        visible: functionType === FunctionType.export,
        component: (
          <InformationType
            types={exportInformationTypes}
            onSelect={this.handleExportInformationTypeSelect}
          />
        ),
        heading: 'Select information type',
        subHeading: 'Choose what you would like to export as CSV.',
        classes: { root: classes.informationType },
        onClose: this.returnToInitialColumn
      },
    ];

    // Panel for Import which contains a sub panel (Import type).
    const importColumns: Array<ColumnConfig> = [
      {
        id: 'Import',
        visible: functionType === FunctionType.import,
        component: (
          <InformationType
            types={importInformationTypes}
            currentType={currentInformationType}
            onSelect={this.handleImportInformationTypeSelect}
            onClose={this.handleInformationTypeClose}
          />
        ),
        heading: 'Select information type',
        subHeading: 'Choose what would you like to import as CSV',
        classes: { root: classes.informationType },
        onClose: this.returnToInitialColumn
      },
      {
        id: 'Import type',
        visible: Boolean(type),
        component: (
          <DataImport
            type={currentInformationType}
          />
        ),
        heading: 'Import from CSV file',
        classes: { root: classes.importType },
        onClose: this.handleInformationTypeClose
      }
    ];

    // Panel for Reports which contains the order selection box.
    const reportColumns: Array<ColumnConfig> = [
      {
        id: 'Report',
        visible: functionType === FunctionType.report,
        component: (
          <Flex direction="column">
            <Flex container>
              {this.fields.map((field) => <Field classes={{ root: classes.selectOrder, label: classes.fieldLabel, component: classes.fieldComponent }} {...field} key={field.label} />)}
            </Flex>
            
            <InformationType
              types={reportInformationTypes}
              onSelect={this.handleReportInformationTypeSelect}
              
            />
            
          </Flex>
        ),
        heading: 'Select information type',
        subHeading: 'Choose what you would like to report as CSV.',
        classes: { root: classes.informationType },
        onClose: this.returnToInitialColumn
      },
    ];

    // Panel for Sync with Myx which contains the order selection box.
    const SyncColumns: Array<ColumnConfig> = [
      {
        id: 'Sync',
        visible: functionType === FunctionType.sync,
        component: (
          <Flex direction="row">
            <Flex container>
              {this.fields.map((field) => 
                <Field classes={{ root: classes.selectOrder, label: classes.fieldLabel, component: classes.fieldComponent }} {...field} key={field.label} />)
              }
            </Flex>
            <Flex container width={40}>            
              <Tooltip title='Sync with MyX'>
                <Button
                  classes={{root: classes.syncButton}}
                  onClick={this.handleSyncWithMyX.bind(this)}
                  endIcon={<Sync />}
                  color='primary'
                  variant='outlined'
                  size='large'
                >
                  Sync
                </Button>
              </Tooltip>
            </Flex>
          </Flex>
        ),
        heading: 'Synchronize with MyX',
        subHeading: 'Update to latest data from MyX.',
        classes: { root: classes.informationType },
        onClose: this.returnToInitialColumn
      },
    ];


    // Define a dict holding the panel components defined above.
    const config = {
      [FunctionType.export]: exportColumns,
      [FunctionType.import]: importColumns,
      [FunctionType.report]: reportColumns,
      [FunctionType.sync]: SyncColumns
    };

    // Show the column whose 'visible' is on (ie. matching functionType).
    return config[functionType].filter(({ visible }) => visible);
  }

  @action handleOrderChange = async (orderId: number) => {
    this.selectedOrderId = orderId;
  }

  @action
  async handleSyncWithMyX(args: MouseEvent) {
    // console.log("*** Update Order id:", this.selectedOrderId, this.orders.slice().map(item => item.label));
    if(!this.selectedOrderId) {
      showNotification('Please select an order first.', NotificationType.error);
      return;
    }
    this.syncing = true; // set flag to show spinner.
    let orderName = this.orders.find(item => item.id == this.selectedOrderId)?.label;
    if(orderName) {
      if(orderName === 'PTI_IT_001') {// Kludge to handle different names used by MyX and VI.
        orderName = 'PTI Italy'
      }
      console.log("*** Send to portal-api:", orderName);

      try {
        const data = await this.aquaStore.syncOrder(orderName);
       
        showNotification(data, NotificationType.info);
      }
      catch(err) {
        showNotification(errObj2Str(err), NotificationType.error);
      }
    }

    this.syncing = false; // clear flag to hide spinner.
  }

  // Setup the Order selection box for "Reports".
  private get fields() {
    //const classes = withStyles.getClasses(this.props) as Record<string, any>;

    // Setup the fields.
    const _fields = [
      {
        label: 'Order',
        component: (
          <Select
            serializerType={SerializerType.number}
            options={this.orders}
            placeholder="Select order"
            value={this.selectedOrderId}
            testAttributeArgs={{
              module: 'DataManagementChoice',
              elementDescription: 'order',
            }}
            onChange={this.handleOrderChange}
          />
        )
      }
    ];

    return _fields;
  }

  private get functionTypesLinks() {
    return [
      {
        text: 'Export',
        type: FunctionType.export,
        onlyForHosting: true
      },
      {
        text: 'Import',
        type: FunctionType.import,
        onlyForHosting: true
      },
      {
        text: 'Reports',
        type: FunctionType.report,
        onlyForHosting: false
      },
      {
        text: 'Sync',
        type: FunctionType.sync,
        onlyForHosting: true
      }      
    ];
  }

  private returnToInitialColumn = (e) => {
    e.preventDefault();

    const { history, rootURL } = this.props;

    history.push(rootURL);
  }

  private startPreparing = () => {
    this.cancelTokenSource = Axios.CancelToken.source();
  }

  private stopPreparing = () => {
    this.cancelTokenSource = undefined;
  }

  // Trigger when a user selects one of the report type (collection, process, ...)
  // It sends a request to download the report from portal-api.
  private handleReportInformationTypeSelect = async (type: ReportInformationType) => {

    if (this.selectedOrderId > 0) {
      this.startPreparing();

      try {
        const blobparts = await this.dataManagementStore.reportData(type, this.selectedOrderId, '', undefined, { cancelToken: this.cancelTokenSource?.token });
        
        if (blobparts) {
          let filename = '';
          if(type == ReportInformationType.customerReport) {
            filename = `${REPORT_INFORMATION_TYPES_LABELS[type]}.xlsx`;
          }
          else {
            filename = `${REPORT_INFORMATION_TYPES_LABELS[type]}.csv`;
          }
          downloadFile(blobparts, filename);
        }

        this.stopPreparing();
      } 
      catch (err) {
        this.stopPreparing();

        showNotification(errObj2Str(err), NotificationType.error);
      }
    }
    else
      showNotification('Please select an order first.', NotificationType.error);
  }

  private handleExportInformationTypeSelect = async (type: ExportInformationType) => {
    this.startPreparing();

    try {
      const blobparts = await this.dataManagementStore.exportData(type, { cancelToken: this.cancelTokenSource?.token });

      if (blobparts) {
        let filename = ``;
        filename = `${EXPORT_INFORMATION_TYPES_LABELS[type]}.csv`;
        downloadFile(blobparts, filename);
      }

      this.stopPreparing();
    } catch (err) {
      this.stopPreparing();

      showNotification(getDefaultError(err?.response?.data?.errors), NotificationType.error);
    }
  }

  private handleImportInformationTypeSelect = (type: ImportInformationType) => {
    setQueries({ type });
  }

  private handleInformationTypeClose = () => {
    deleteQueries(['type']);
  }

  private handleCancelPreparing = () => {
    this.cancelTokenSource?.cancel();
  }

  render() {
    const { rootURL, match: { params: { functionType } } } = this.props;
    const { loading } = this.dataManagementStore;
    const { isHostingCompany } = this.authStore.user;
    const classes = withStyles.getClasses(this.props) as Record<string, any>;

    console.log("******************** DataManChoice functionType:", rootURL, functionType, this.syncing);

    return (
      <Grid container>
        {loading && <Preparing onCancel={this.handleCancelPreparing} functionType={functionType === undefined ? FunctionType.export : FunctionType[functionType]} />}

        {this.syncing && <Loading absolute/>}

        <Column // Select function panel (Import/Export/Reports/Sync)
          heading="Select function"
          classes={{ root: classes.functionType }}
        >
          {this.functionTypesLinks.map(({ type, text, onlyForHosting }) => (
            <NavLink
              key={text}
              to={`${rootURL}/${type}`}
              className={classes.functionTypeLink}
              activeClassName={classes.functionTypeLinkActive}
              style={{pointerEvents: (!onlyForHosting || isHostingCompany) ? 'auto' : 'none', opacity: (!onlyForHosting || isHostingCompany) ? 'inherit' : '0.3'}}
            >
              <span className={classes.functionTypeLinkText}>{text}</span>
              {type === functionType && (
                <CloseIcon
                  className={classes.closeFunctionIcon}
                  onClick={this.returnToInitialColumn}
                />
              )}
            </NavLink>
          ))}
        </Column>
        {this.functionColumns.length === 0 && (
          <Card // Initial blank panel with hint text.
            classes={{ root: classes.notSelectedFunctionMessageWrapper }}
          >
            <Message
              classes={{
                root: classes.notSelectedFunctionMessage
              }}
              icon={<EmptyDataManagementIcon />}
              heading="Please select a function from the list on the left."
              subheading=""
            />
          </Card>
        )}
        {this.functionColumns.map((column) => (
          <Column
            key={column.id}
            heading={column.heading}
            subHeading={column.subHeading}
            classes={{
              ...column.classes,
              root: cx(classes.functionTypeColumn, column.classes?.root)
            }}
            onClose={column.onClose}
          >
            {column.component}
          </Column>
        ))}
      </Grid>
    );
  }
}

const activeFunctionTypeLinkStyles = {
  color: themev5.palette.colors.primaryAccent,
  backgroundColor: alpha(themev5.palette.colors.primaryAccent, .04),
  borderColor: themev5.palette.colors.primaryAccent,

  '& $functionTypeLinkText': {
    borderColor: 'transparent',
  }
};
const functionTypeSiblingStyles = {
  borderTopLeftRadius: 0,
  borderBottomLeftRadius: 0,
  boxShadow: '-4px 0px 8px rgba(0, 0, 0, 0.08)',
  borderLeft: 'none',
  marginLeft: -themev5.spacing(),
};
const functionTypeColumnWidth = 272;
const informationTypeColumnWidth = 310;
const resultColumnWidth = `calc(100% - (${functionTypeColumnWidth}px + ${informationTypeColumnWidth}px))`;

const DataManagementChoiceStyled = withStyles(
  DataManagementChoice, 
  (theme, props) => ({
    "root": {},
    "functionTypeLink": {
      padding: theme.spacing(2, 6, 2, 4),
      height: 40,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      borderLeft: '2px solid transparent',

      '&:hover': activeFunctionTypeLinkStyles
    },
    "functionTypeLinkActive": activeFunctionTypeLinkStyles,
    "functionTypeLinkText": {
      ...theme.shortcuts.borderPrimaryDashed,
    },
    "closeFunctionIcon": {
      fontSize: 14,

      '& path': {
        fill: theme.palette.colors.grey,
      },
    },
    "functionTypeColumn": functionTypeSiblingStyles,
    "notSelectedFunctionMessageWrapper": {
      ...functionTypeSiblingStyles,
      flexGrow: 1,
    },
    "notSelectedFunctionMessage": {
      height: '100%',
    },
    "functionType": {
      width: functionTypeColumnWidth,
      wrap: 'nowrap',
    },
    "informationType": {
      width: informationTypeColumnWidth,
      flexGrow: 1,
    },
    "importType": {
      width: resultColumnWidth,
    },
    "selectOrder": {
      padding: theme.spacing(0, 0, 2, 4),
      width: informationTypeColumnWidth,
    },
    "fieldLabel": {
      width: '20%',
    },
    "fieldComponent": {
      width: '75%'
    },
    "syncButton": {
      margin: theme.spacing(5, 0, 0, 2),
    }
  })
);

export default DataManagementChoiceStyled;